import { forwardRef, useCallback, useState } from 'react';

// --- Icons ---
import { ChevronDownIcon } from 'shared-components/components/TipTap/tiptap-icons/chevron-down-icon';
import { NormalTextIcon } from 'shared-components/components/TipTap/tiptap-icons/normal-text-icon';

// --- Hooks ---
import { useTiptapEditor } from 'shared-components/hooks/use-tiptap-editor';

// --- Tiptap UI ---
import {
  HeadingButton,
  setToParagraph,
  canToggle as canToggleHeading,
} from 'shared-components/components/TipTap/tiptap-ui/heading-button';
import type { UseHeadingDropdownMenuConfig } from 'shared-components/components/TipTap/tiptap-ui/heading-dropdown-menu';
import { useHeadingDropdownMenu } from 'shared-components/components/TipTap/tiptap-ui/heading-dropdown-menu';

// --- UI Primitives ---
import type { ButtonProps } from 'shared-components/components/TipTap/tiptap-ui-primitive/button';
import { Button, ButtonGroup } from 'shared-components/components/TipTap/tiptap-ui-primitive/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from 'shared-components/components/TipTap/tiptap-ui-primitive/dropdown-menu';
import { Card, CardBody } from 'shared-components/components/TipTap/tiptap-ui-primitive/card';

export interface HeadingDropdownMenuProps extends Omit<ButtonProps, 'type'>, UseHeadingDropdownMenuConfig {
  /**
   * Whether to render the dropdown menu in a portal
   * @default false
   */
  portal?: boolean;
  /**
   * Callback for when the dropdown opens or closes
   */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Dropdown menu component for selecting heading levels in a Tiptap editor.
 *
 * For custom dropdown implementations, use the `useHeadingDropdownMenu` hook instead.
 */
export const HeadingDropdownMenu = forwardRef<HTMLButtonElement, HeadingDropdownMenuProps>(
  (
    {
      editor: providedEditor,
      levels = [1, 2, 3, 4, 5, 6],
      hideWhenUnavailable = false,
      showNormalText = false,
      portal = false,
      onOpenChange,
      ...buttonProps
    },
    ref,
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { isVisible, isActive, isNormalText, canToggle, Icon, label } = useHeadingDropdownMenu({
      editor,
      levels,
      hideWhenUnavailable,
      showNormalText,
    });

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (!editor || !canToggle) return;
        setIsOpen(open);
        onOpenChange?.(open);
      },
      [canToggle, editor, onOpenChange],
    );

    if (!isVisible) {
      return null;
    }

    return (
      <DropdownMenu modal open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            data-style="ghost"
            data-active-state={isActive || isNormalText ? 'on' : 'off'}
            role="button"
            tabIndex={-1}
            disabled={!canToggle}
            data-disabled={!canToggle}
            aria-label={label}
            aria-pressed={isActive || isNormalText}
            tooltip={label}
            {...buttonProps}
            ref={ref}>
            <Icon className="tiptap-button-icon" />
            <ChevronDownIcon className="tiptap-button-dropdown-small" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" portal={portal}>
          <Card>
            <CardBody>
              <ButtonGroup>
                {showNormalText && (
                  <DropdownMenuItem key="normal-text" asChild>
                    <Button
                      type="button"
                      data-style="ghost"
                      data-active-state={isNormalText ? 'on' : 'off'}
                      role="menuitem"
                      tabIndex={-1}
                      aria-label="Normal text"
                      aria-pressed={isNormalText}
                      disabled={!editor || !canToggleHeading(editor, undefined)}
                      data-disabled={!editor || !canToggleHeading(editor, undefined)}
                      onClick={() => setToParagraph(editor ?? null)}>
                      <NormalTextIcon className="tiptap-button-icon" />
                      <span className="tiptap-button-text">Normal text</span>
                    </Button>
                  </DropdownMenuItem>
                )}
                {levels.map((level) => (
                  <DropdownMenuItem key={`heading-${level}`} asChild>
                    <HeadingButton editor={editor} level={level} text={`Heading ${level}`} showTooltip={false} />
                  </DropdownMenuItem>
                ))}
              </ButtonGroup>
            </CardBody>
          </Card>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);

HeadingDropdownMenu.displayName = 'HeadingDropdownMenu';

export default HeadingDropdownMenu;
